import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { useContest } from '~/domains/contest/hooks/useContest';

import { useGetBranding } from '../../hooks/useGetBranding';

function useIsShouldSkipKycAfterSignUp() {
  const router = useRouter();
  const { redirectTo, requireKyc } = router.query;
  const { isEOBrand } = useGetBranding();

  // `requireKyc=1` forces the KYC form, overriding the contest-based skip
  // heuristic below (Contest Vault withdraw URLs carry a `contest_…` id that
  // would otherwise skip KYC — EDGE-31783). Safe to honor unconditionally: it
  // can only force KYC, never skip it. Any `1` wins, so a mixed
  // `requireKyc=0&requireKyc=1` still shows the form.
  const isKycRequired = Array.isArray(requireKyc) ? requireKyc.includes('1') : requireKyc === '1';

  const isContestCreation = useMemo(() => {
    if (!redirectTo) return false;
    return (redirectTo as string).startsWith('contest/create');
  }, [redirectTo]);

  const contestId = useMemo(() => {
    if (!redirectTo) {
      return;
    }
    const parts = (redirectTo as string).split('/');
    if (parts[1] !== 'contest') {
      return;
    }

    return parts[2];
  }, [redirectTo]);

  const contestIdV2 = (redirectTo as string)?.match(/contest_[A-Za-z0-9]*/)?.[0];

  const contest = useContest(contestId);

  return useMemo(
    () => ({
      isShouldSkipKycAfterSignUpLoading:
        !isKycRequired &&
        !isContestCreation &&
        !isEOBrand &&
        contest.isLoading &&
        contest.isFetching,
      isShouldSkipKycAfterSignUp:
        !isKycRequired &&
        (isContestCreation ||
          isEOBrand ||
          contestIdV2 ||
          (contest.data?.settings?.entertainmentOnlySettings?.enabled ?? false)),
      contest,
    }),
    [
      isKycRequired,
      isContestCreation,
      isEOBrand,
      contestIdV2,
      contest.data,
      contest.isLoading,
      contest.isFetching,
    ]
  );
}

export default useIsShouldSkipKycAfterSignUp;
